import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authAxios } from '../../contexts/AuthContext';
import { Clock, Truck, Package, Check, X, RotateCcw, Eye, MessageCircle, Mail, Printer, SkipForward, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { useBizInfo } from '../../contexts/BizInfoContext'; // ← ADD
import { t } from '../../lib/translations';
import { toast } from 'sonner';
import Pagination from '../../components/Pagination';
import OrderBill from '../../components/OrderBill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending:    { icon: Clock,      color: 'bg-yellow-100 text-yellow-800' },
  confirmed:  { icon: Check,      color: 'bg-blue-100 text-blue-800' },
  processing: { icon: Package,    color: 'bg-purple-100 text-purple-800' },
  shipped:    { icon: Truck,      color: 'bg-indigo-100 text-indigo-800' },
  delivered:  { icon: Check,      color: 'bg-green-100 text-green-800' },
  cancelled:  { icon: X,          color: 'bg-red-100 text-red-800' },
  returned:   { icon: RotateCcw,  color: 'bg-gray-100 text-gray-800' }
};

const statusOptions = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'returned'];

const returnStatusLabels = {
  return_requested: 'Return Requested', return_approved: 'Return Approved',
  return_product_picked_up: 'Product Picked Up', return_quality_check: 'Quality Check',
  return_refund_processed: 'Refund Processed', return_rejected: 'Return Rejected'
};
const returnStatusLabelsHi = {
  return_requested: 'वापसी अनुरोधित', return_approved: 'वापसी स्वीकृत',
  return_product_picked_up: 'उत्पाद उठाया गया', return_quality_check: 'गुणवत्ता जांच',
  return_refund_processed: 'रिफंड प्रोसेस्ड', return_rejected: 'वापसी अस्वीकृत'
};

export default function AdminOrders() {
  const { language, token } = useAuth();
  const { bizInfo } = useBizInfo(); // ← ADD

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [billOrder, setBillOrder] = useState(null);
  const [thermalMode, setThermalMode] = useState(true); // 80mm thermal default

  // Skip items state
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipOrder, setSkipOrder] = useState(null);
  const [itemsToSkip, setItemsToSkip] = useState({});
  const [skipping, setSkipping] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/orders?page=${currentPage}&page_size=20`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      const response = await authAxios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data;
      setOrders(data.orders || data);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, currentPage, token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [currentPage]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await authAxios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(language === 'hi' ? 'स्थिति अपडेट की गई' : 'Status updated');
      if (response.data.whatsapp_link) setWhatsappLink(response.data.whatsapp_link);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReturnStatusChange = async (orderId, newReturnStatus) => {
    try {
      await authAxios.put(`${API}/admin/orders/${orderId}/return-status`, { return_status: newReturnStatus, admin_notes: '' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(language === 'hi' ? 'वापसी स्थिति अपडेट की गई' : 'Return status updated');
      fetchOrders();
      if (selectedOrder) {
        const updated = await authAxios.get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
        setSelectedOrder(updated.data);
      }
    } catch (error) {
      toast.error('Failed to update return status');
    }
  };

  // ── Skip Items ──
  const openSkipDialog = (order) => {
    setSkipOrder(order);
    const initial = {};
    order.items.filter(i => !i.skipped).forEach(item => {
      initial[item.product_id] = { checked: false, reason: 'Out of stock', name: item.name };
    });
    setItemsToSkip(initial);
    setShowSkipDialog(true);
  };

  const handleSkipSubmit = async () => {
    const toSkip = Object.entries(itemsToSkip)
      .filter(([, v]) => v.checked)
      .map(([product_id, v]) => ({ product_id, reason: v.reason }));

    if (toSkip.length === 0) { toast.error('Select at least one item to skip'); return; }

    const activeItems = skipOrder.items.filter(i => !i.skipped);
    if (toSkip.length === activeItems.length) {
      toast.error('Cannot skip all items. Cancel the order instead.');
      return;
    }

    setSkipping(true);
    try {
      const response = await authAxios.put(
        `${API}/admin/orders/${skipOrder.id}/skip-items`,
        { skip_items: toSkip },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const message  = response.data?.message ?? 'Items skipped successfully';
      const newTotal = response.data?.new_total != null ? `₹${Number(response.data.new_total).toFixed(2)}` : '';
      toast.success(<div><p>{message}</p>{newTotal && <p className="text-xs mt-1">New total: {newTotal}</p>}</div>);
      setShowSkipDialog(false);
      setSkipOrder(null);
      fetchOrders();
      if (selectedOrder?.id === skipOrder.id) {
        const updated = await authAxios.get(`${API}/orders/${skipOrder.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSelectedOrder(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip items');
    } finally {
      setSkipping(false);
    }
  };

  const formatPrice = (price) => `₹${price.toFixed(2)}`;
  const formatDate  = (dateStr) => new Date(dateStr).toLocaleDateString(
    language === 'hi' ? 'hi-IN' : 'en-IN',
    { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('orders', language)}</h1>
        <div className="flex items-center gap-3">
          {/* Optional thermal toggle */}
          <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
            <input type="checkbox" checked={thermalMode} onChange={e => setThermalMode(e.target.checked)} className="w-3.5 h-3.5" />
            80mm Thermal
          </label>
          <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val); setCurrentPage(1); }}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Filter by status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'hi' ? 'सभी ऑर्डर' : 'All Orders'}</SelectItem>
              {statusOptions.map(s => <SelectItem key={s} value={s}>{t(s, language)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">Order ID</th>
                <th className="text-left p-4 font-medium text-slate-600">Customer</th>
                <th className="text-left p-4 font-medium text-slate-600">Date</th>
                <th className="text-left p-4 font-medium text-slate-600">Items</th>
                <th className="text-left p-4 font-medium text-slate-600">Total</th>
                <th className="text-left p-4 font-medium text-slate-600">Status</th>
                <th className="text-right p-4 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const status    = statusConfig[order.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                const hasSkipped = order.items?.some(i => i.skipped);
                const canSkip    = ['pending', 'confirmed', 'processing'].includes(order.status);
                return (
                  <tr key={order.id} className={`border-b border-slate-100 hover:bg-slate-50 ${order.return_status ? 'bg-orange-50/30' : ''}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs" title={order.id}>{order.id.slice(0, 8)}...</span>
                        {order.return_status && <RotateCcw className="w-4 h-4 text-orange-600" />}
                        {hasSkipped && <AlertTriangle className="w-4 h-4 text-amber-500" title="Has skipped items" />}
                      </div>
                      {order.return_status && (
                        <Badge className="mt-1 text-xs bg-orange-100 text-orange-700">
                          {language === 'hi' ? returnStatusLabelsHi[order.return_status] : returnStatusLabels[order.return_status]}
                        </Badge>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-sm text-slate-900">{order.user_name || 'N/A'}</p>
                      <p className="text-xs text-slate-500">{order.phone || ''}</p>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{formatDate(order.created_at)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        {order.items?.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="relative">
                            <img src={item.image} alt="" className={`w-8 h-8 rounded object-cover ${item.skipped ? 'opacity-30 grayscale' : ''}`} />
                            {item.skipped && <X className="absolute -top-1 -right-1 w-3 h-3 text-red-500 bg-white rounded-full" />}
                          </div>
                        ))}
                        {order.items?.length > 2 && <span className="text-sm text-slate-500">+{order.items.length - 2}</span>}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-bold text-green-600">{formatPrice(order.total)}</span>
                      {hasSkipped && <p className="text-xs text-amber-600">Partial</p>}
                    </td>
                    <td className="p-4">
                      <Select value={order.status} onValueChange={(value) => handleStatusChange(order.id, value)}>
                        <SelectTrigger className={`w-36 h-8 ${status.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" /><SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map(s => <SelectItem key={s} value={s}>{t(s, language)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canSkip && (
                          <Button variant="outline" size="sm" onClick={() => openSkipDialog(order)} title="Skip unavailable items" className="text-amber-600 border-amber-300 hover:bg-amber-50">
                            <SkipForward className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setBillOrder(order)} title="Print Bill">
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(order)}>
                          <Eye className="w-4 h-4 mr-1" />View
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && (
          <div className="text-center py-12 text-slate-500">{language === 'hi' ? 'कोई ऑर्डर नहीं मिला' : 'No orders found'}</div>
        )}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} language={language} />
      </div>

      {/* ── Order Detail Dialog ── */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{t('orderDetails', language)}</span>
              <Button variant="outline" size="sm" onClick={() => { setBillOrder(selectedOrder); setSelectedOrder(null); }}>
                <Printer className="w-4 h-4 mr-1" />{language === 'hi' ? 'बिल' : 'Print Bill'}
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Order ID</span><span className="font-mono text-xs">{selectedOrder.id}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span>{formatDate(selectedOrder.created_at)}</span></div>

              {['pending', 'confirmed', 'processing'].includes(selectedOrder.status) && (
                <Button variant="outline" size="sm" className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={() => { openSkipDialog(selectedOrder); setSelectedOrder(null); }}>
                  <SkipForward className="w-4 h-4 mr-2" />
                  {language === 'hi' ? 'अनुपलब्ध आइटम छोड़ें' : 'Skip Unavailable Items'}
                </Button>
              )}

              <div className="border-t border-slate-100 pt-4">
                <p className="text-sm text-slate-500 mb-2">{t('items', language)}</p>
                <div className="max-h-48 overflow-y-auto pr-2 space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-3 py-2 ${item.skipped ? 'opacity-50' : ''}`}>
                      <img src={item.image} alt="" className={`w-10 h-10 rounded object-cover ${item.skipped ? 'grayscale' : ''}`} />
                      <div className="flex-1">
                        <p className={`font-medium text-sm ${item.skipped ? 'line-through text-slate-400' : ''}`}>{item.name}</p>
                        <p className="text-xs text-slate-500">{item.quantity} x {formatPrice(item.price)}</p>
                        {item.skipped && <p className="text-xs text-red-500">Skipped: {item.skip_reason}</p>}
                      </div>
                      <span className={`font-bold text-sm ${item.skipped ? 'line-through text-slate-400' : ''}`}>{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <div className="flex justify-between font-bold">
                  <span>{t('total', language)}</span>
                  <span className="text-green-600">{formatPrice(selectedOrder.total)}</span>
                </div>
                {selectedOrder.has_skipped_items && <p className="text-xs text-amber-600 mt-1">* Total adjusted due to skipped items</p>}
              </div>

              <div className="border-t border-slate-100 pt-4 text-sm">
                <p className="text-slate-500 mb-1">{t('deliveryAddress', language)}</p>
                <p>{selectedOrder.delivery_address}</p>
                <p>Pincode: {selectedOrder.delivery_pincode}</p>
                <p>Phone: {selectedOrder.phone}</p>
                {selectedOrder.notes && <p className="mt-2 text-slate-500">Notes: {selectedOrder.notes}</p>}
              </div>

              {selectedOrder.return_status && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <RotateCcw className="w-4 h-4 text-orange-600" />
                    <p className="font-semibold text-sm">{language === 'hi' ? 'वापसी की स्थिति' : 'Return Status'}</p>
                  </div>
                  {selectedOrder.is_partial_return && (
                    <div className="mb-3 p-2 bg-amber-50 rounded text-sm text-amber-700">Partial return — only selected items</div>
                  )}
                  {selectedOrder.return_items?.length > 0 && (
                    <div className="mb-3 bg-slate-50 rounded p-3">
                      <p className="text-xs font-medium mb-2">Items in return:</p>
                      {selectedOrder.return_items.map((ri, idx) => (
                        <div key={idx} className="text-xs text-slate-600 flex justify-between">
                          <span>{ri.name} × {ri.quantity}</span><span>₹{(ri.price * ri.quantity).toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Select value={selectedOrder.return_status} onValueChange={(value) => handleReturnStatusChange(selectedOrder.id, value)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(returnStatusLabels).map(([val, label]) => (
                        <SelectItem key={val} value={val}>{language === 'hi' ? returnStatusLabelsHi[val] : label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedOrder.return_history?.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3 mt-3">
                      <p className="text-xs font-medium text-slate-700 mb-2">Return History</p>
                      {[...selectedOrder.return_history].reverse().map((entry, idx) => (
                        <div key={idx} className="text-xs border-b border-slate-100 pb-2 last:border-0">
                          <div className="flex justify-between">
                            <span className="font-medium">{returnStatusLabels[entry.status] || entry.status}</span>
                            <span className="text-slate-500">{new Date(entry.timestamp).toLocaleDateString()}</span>
                          </div>
                          {entry.reason && <p className="text-slate-600 mt-1">Reason: {entry.reason}</p>}
                          {entry.admin_notes && <p className="text-slate-500">Note: {entry.admin_notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Skip Items Dialog ── */}
      <Dialog open={showSkipDialog} onOpenChange={() => setShowSkipDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SkipForward className="w-5 h-5 text-amber-500" />
              {language === 'hi' ? 'अनुपलब्ध आइटम छोड़ें' : 'Skip Unavailable Items'}
            </DialogTitle>
          </DialogHeader>
          {skipOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Skipped items will be removed from the order. Stock will be automatically restored and total recalculated.</span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {skipOrder.items.filter(i => !i.skipped).map((item) => (
                  <div key={item.product_id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-3 mb-2">
                      <input type="checkbox" id={`skip-${item.product_id}`}
                        checked={itemsToSkip[item.product_id]?.checked || false}
                        onChange={(e) => setItemsToSkip(prev => ({ ...prev, [item.product_id]: { ...prev[item.product_id], checked: e.target.checked } }))}
                        className="w-4 h-4 text-amber-600 rounded" />
                      <img src={item.image} alt="" className="w-10 h-10 rounded object-cover" />
                      <div className="flex-1">
                        <label htmlFor={`skip-${item.product_id}`} className="font-medium text-sm cursor-pointer">{item.name}</label>
                        <p className="text-xs text-slate-500">{item.quantity} × ₹{item.price} = ₹{(item.price * item.quantity).toFixed(0)}</p>
                      </div>
                    </div>
                    {itemsToSkip[item.product_id]?.checked && (
                      <div className="ml-7">
                        <Label className="text-xs">Reason</Label>
                        <Select value={itemsToSkip[item.product_id]?.reason || 'Out of stock'}
                          onValueChange={(val) => setItemsToSkip(prev => ({ ...prev, [item.product_id]: { ...prev[item.product_id], reason: val } }))}>
                          <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Out of stock">Out of stock</SelectItem>
                            <SelectItem value="Quality issue">Quality issue</SelectItem>
                            <SelectItem value="Not available today">Not available today</SelectItem>
                            <SelectItem value="Damaged">Damaged</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {Object.values(itemsToSkip).some(v => v.checked) && (
                <div className="p-3 bg-green-50 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span>New Total:</span>
                    <span className="font-bold text-green-600">
                      ₹{skipOrder.items.filter(i => !i.skipped && !itemsToSkip[i.product_id]?.checked).reduce((sum, i) => sum + i.price * i.quantity, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSkipDialog(false)}>Cancel</Button>
            <Button onClick={handleSkipSubmit} disabled={skipping} className="bg-amber-500 hover:bg-amber-600 text-white">
              {skipping ? 'Skipping...' : 'Skip Selected Items'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── WhatsApp Dialog ── */}
      <Dialog open={!!whatsappLink} onOpenChange={() => setWhatsappLink(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              {language === 'hi' ? 'WhatsApp पर सूचना भेजें' : 'Send WhatsApp Notification'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <Mail className="w-5 h-5 text-green-600" />
              <span className="text-green-700 text-sm">{language === 'hi' ? 'ईमेल सूचना भेजी गई!' : 'Email notification sent!'}</span>
            </div>
            <Button onClick={() => { window.open(whatsappLink, '_blank', 'noopener,noreferrer'); setWhatsappLink(null); }} className="w-full bg-green-500 hover:bg-green-600 text-white">
              <MessageCircle className="w-5 h-5 mr-2" />{language === 'hi' ? 'WhatsApp पर भेजें' : 'Send on WhatsApp'}
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setWhatsappLink(null)}>{language === 'hi' ? 'बाद में' : 'Skip for now'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Bill Dialog ── bizInfo from context, no extra fetch ── */}
      <OrderBill
        order={billOrder}
        open={!!billOrder}
        onClose={() => setBillOrder(null)}
        isAdmin={true}
        language={language}
        bizInfo={bizInfo}
        thermalMode={thermalMode}
      />
    </div>
  );
}
